<template>
  <div class="contracts-modern">
    <!-- Header Section -->
    <div class="contracts-header">
      <div class="header-content">
        <div class="title-section">
          <h2 class="section-title">
            <i class="fas fa-file-contract title-icon"></i>
            Contratos
          </h2>
          <p class="section-subtitle">Gerencie os contratos do residente</p>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="contracts-content">
      <!-- Table Section -->
      <div class="table-section">
        <div class="table-card">
          <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
            <!-- No Data State -->
            <div v-if="contracts.length === 0 && !loading" class="no-results">
              <i class="fas fa-file-contract no-results-icon"></i>
              <h4 class="no-results-title">Nenhum contrato encontrado</h4>
            </div>

            <!-- Data Table -->
            <v-data-table
              v-if="contracts.length > 0 || loading"
              :headers="headers"
              :items="contracts"
              item-key="id"
              :footer-props="{
                'items-per-page-options': [25, 50, 75, 100],
                'items-per-page-text': 'Mostrar',
              }"
              class="modern-table"
              :no-data-text="'Nenhum contrato encontrado'"
              :loading-text="'Carregando contratos...'"
            >
              <!-- Status Column -->
              <template v-slot:[`item.active`]="{ item }">
                <div class="status-cell">
                  <span
                    class="status-badge"
                    :class="
                      item.active == 1 ? 'status-active' : 'status-inactive'
                    "
                  >
                    <i
                      :class="[
                        'fas status-icon',
                        item.active == 1
                          ? 'fa-check-circle'
                          : 'fa-times-circle',
                      ]"
                    ></i>
                    {{ item.active == 1 ? "Ativo" : "Inativo" }}
                  </span>
                </div>
              </template>

              <!-- User Column -->
              <template v-slot:[`item.user_name`]="{ item }">
                <div class="user-cell">
                  <div class="user-avatar">
                    <img
                      v-if="item.user_photo_url"
                      :src="item.user_photo_url"
                      :alt="item.user_name"
                      class="user-photo"
                    />
                    <i v-else class="fas fa-user"></i>
                  </div>
                  <div class="user-info">
                    <span class="user-name">{{ item.user_name }}</span>
                  </div>
                </div>
              </template>

              <!-- Contract Name Column -->
              <template v-slot:[`item.name`]="{ item }">
                <div class="contract-name-cell">
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="contract-name">
                        {{ item.name }}
                      </span>
                    </template>
                    <span>{{ item.name }}</span>
                  </v-tooltip>
                </div>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.action`]="{ item }">
                <div class="actions-cell">
                  <v-btn
                    class="btn-modern btn-modern-info"
                    x-small
                    @click="modalViewContract(item.id)"
                  >
                    <i class="fas fa-external-link-alt mr-1"></i>
                    Visualizar
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </b-overlay>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <ViewModal :resident_id="resident_id" :contract_id="contract_id" />
  </div>
</template>

<script>
import ViewModal from "../../templates/Contract/ViewModal";

import api from "../../services/api";

import { formatDate } from "../../lib/format";

import routes from "../../routes";

export default {
  name: "MenuContract",
  components: {
    ViewModal,
  },
  props: {
    resident_id: Number,
  },
  data() {
    return {
      loading: false,
      contracts: [],
      contract_id: null,
      headers: [
        {
          text: "STATUS",
          value: "active",
          width: "120px",
        },
        {
          text: "CADASTRADO POR",
          value: "user_name",
          width: "200px",
        },
        {
          text: "NOME DO CONTRATO",
          value: "name",
        },
        {
          text: "AÇÕES",
          value: "action",
          sortable: false,
          width: "150px",
        },
      ],
    };
  },
  watch: {
    async resident_id() {
      await this.getContracts();
    },
  },

  mounted() {
    this.getContracts();
  },

  methods: {
    formatDate,

    async getContracts() {
      try {
        this.loading = true;
        const { resident_id } = this;
        let params = { resident_id };

        const response = await api.get("/contract/resident", {
          params,
        });

        const { error, contracts } = response.data;

        if (!error) {
          this.contracts = contracts;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },

    modalViewContract(contract_id) {
      let { resident_id } = this;
      this.contract_id = contract_id;
      let route = routes.resolve({
        name: "contract-print",
        params: {
          contract_id,
          resident_id,
        },
      });

      window.open(route.href, "_blank");
    },
  },
};
</script>

<style scoped>
.contracts-modern {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Section */
.contracts-header {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.title-section {
  flex: 1;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 20px;
}

.section-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 14px;
}

.actions-section {
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn-add-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

.btn-create-first {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
  margin-top: 16px !important;
}

.btn-create-first:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

/* Content Section */
.contracts-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Table Section */
.table-section {
  margin-bottom: 0;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 200px;
}

/* No Results State */
.no-results {
  text-align: center;
  padding: 60px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Table Cell Styling */
.status-cell {
  display: flex;
  align-items: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  gap: 4px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.status-icon {
  font-size: 10px;
}

/* User Cell */
.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

/* Contract Name Cell */
.contract-name-cell {
  max-width: 300px;
}

.contract-name {
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  display: block;
}

/* Actions Cell */
.actions-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.view-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: white !important;
  border: none !important;
}

.view-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
}

/* Table Styling */
.modern-table {
  background: transparent !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .contracts-header {
    padding: 20px 16px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .section-title {
    font-size: 20px;
    justify-content: center;
  }

  .section-subtitle {
    text-align: center;
  }

  .actions-section {
    justify-content: center;
  }

  .user-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }

  .no-results {
    padding: 40px 16px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 18px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 18px;
    flex-direction: column;
    gap: 8px;
  }

  .title-icon {
    font-size: 18px;
  }

  .btn-add-modern,
  .btn-create-first {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .status-badge {
    font-size: 10px;
    padding: 4px 8px;
  }

  .contract-name-cell {
    max-width: 200px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
  padding: 16px !important;
}

.modern-table >>> .v-data-table tbody tr {
  transition: all 0.2s ease !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-table >>> .v-data-table tbody td {
  padding: 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>